import { useCallback, useEffect, useState } from "react";
import { type API } from "../logic/classApi";
import { createUrl } from "./apiCommon";
import { showErrorMessage } from "../components/appPane";
import { MESSAGES } from "../constants/const";
import type { Chemical, ChemicalType, ChemicalVendor } from "../types";

/** 薬剤販売業者情報APIのURL */
const API_CHEMICAL_VENDOR_URL = import.meta.env.VITE_API_CHEMICAL_VENDOR_URL;

/** 薬剤分類情報APIのURL */
const API_CHEMICAL_TYPE_URL = import.meta.env.VITE_API_CHEMICAL_TYPE_URL;

/** 薬剤情報APIのURL */
const API_CHEMICAL_URL = import.meta.env.VITE_API_CHEMICAL_URL;

/** 薬剤販売業者情報 */
interface VendorData {
  /** 支所コード */
  branchCode: string;
  /** 薬剤販売業者ID */
  vendorId: number;
  /** 事業者名 */
  vendorName: string;
  /** 郵便番号 */
  vendorPost: string;
  /** 住所１ */
  vendorAddress1: string;
  /** 住所２ */
  vendorAddress2: string;
  /** 電話番号 */
  vendorTel: string;
  /** FAX番号 */
  vendorFax: string;
  /** 担当者名 */
  vendorPerson: string;
  /** 担当者電話番号 */
  vendorPersonTel: string;
  /** 担当者メールアドレス */
  vendorPersonMail: string;
  /** 土日祝搬入可能日 */
  vendorDeliverySupportDays: number;
  /** リードタイム */
  vendorLeadTime: number;
  /** メモ */
  vendorMemo: string;
  /** 作成日時 */
  createdAt?: string;
  /** 更新日時 */
  updatedAt?: string;
  /** 削除日時 */
  deletedAt?: string;
}

/** 薬剤販売業者情報 */
interface VendorDataForm {
  /** 支所コード */
  branchCode: string;
  /** 薬剤販売業者ID */
  vendorId?: number;
  /** 事業者名 */
  vendorName: string;
  /** 郵便番号 */
  vendorPost: string;
  /** 住所１ */
  vendorAddress1: string;
  /** 住所２ */
  vendorAddress2: string;
  /** 電話番号 */
  vendorTel: string;
  /** FAX番号 */
  vendorFax: string;
  /** 担当者名 */
  vendorPerson: string;
  /** 土日祝搬入可能日 */
  vendorDeliverySupportDays: number;
  /** リードタイム */
  vendorLeadTime: number;
  /** メモ */
  vendorMemo: string;
}

/** 薬剤分類情報 */
interface TypeData {
  /** 支所コード */
  branchCode: string;
  /** 薬剤分類ID */
  chemicalTypeId: number;
  /** 薬剤分類名 */
  chemicalTypeName: string;
  /** 作成日時 */
  createdAt?: string;
  /** 更新日時 */
  updatedAt?: string;
  /** 削除日時 */
  deletedAt?: string;
}

/** 薬剤分類情報 */
interface TypeDataForm {
  /** 支所コード */
  branchCode: string;
  /** 薬剤分類ID */
  chemicalTypeId?: number;
  /** 薬剤分類名 */
  chemicalTypeName: string;
}

/** 薬剤情報 */
interface ChemicalData {
  /** 支所コード */
  branchCode: string;
  /** 薬剤ID */
  chemicalId: number;
  /** 薬剤販売業者ID */
  vendorId: number;
  /** 追加販売業者IDリスト */
  additionalVendorId?: number[];
  /** 薬剤分類ID */
  chemicalTypeId: number;
  /** 薬剤名（画面表示用） */
  chemicalName: string;
  /** 薬剤名（発注用） */
  chemicalName1: string;
  /** 薬剤名（Excel資料２） */
  chemicalName2: string;
  /** 薬剤名（Excel資料３） */
  chemicalName3: string;
  /** ソート順（画面表示用） */
  chemicalSort1: number;
  /** ソート順（Excel資料２） */
  chemicalSort2: number;
  /** ソート順（Excel資料３） */
  chemicalSort3: number;
  /** 品目コード */
  chemicalCode: string;
  /** WBS要素 */
  chemicalWbs: string;
  /** 単位 */
  chemicalUnit1: string;
  /** 発注単位 */
  chemicalUnit2: string;
  /** 発注単位補正値 */
  chemicalUnit2Calc: number;
  /** 転記単位 */
  chemicalUnit3: string;
  /** 転記単位補正値 */
  chemicalUnit3Calc: number;
  /** 管理用途 */
  chemicalViewtype: number;
  /** 保管貯槽数 */
  chemicalStorageCount: number;
  /** アイコンイメージ１ */
  chemicalIcon1: number;
  /** アイコンイメージ２ */
  chemicalIcon2: number;
  /** 設計原単位 */
  chemicalIntensity: number;
  /** 平均実績原単位 */
  chemicalIntensityAverage: number;
  /** 在庫上限 */
  chemicalLimit: number;
  /** 注意レベル */
  chemicalBorder1: number;
  /** 発注レベル */
  chemicalBorder2: number;
  /** 貯槽在庫上限リスト */
  chemicalStockLimit: number[];
  /** 保管貯槽 補給レベル */
  chemicalStockBorder1: number[];
  /** 保管貯槽 発注レベル */
  chemicalStockBorder2: number[];
  /** 発注量 */
  chemicalOrdervolume: number;
  /** 最低発注量 */
  chemicalMinOrdervolume: number;
  /** 不燃粗大施設稼働時使用量 */
  chemicalCalc01: number;
  /** 使用量予測算出方法 */
  chemicalCalctype: number;
  /** 在庫下限 */
  chemicalCalc11: number;
  /** 共通停炉時使用量 */
  chemicalCalc12: number;
  /** 定期使用量（１日） */
  chemicalCalc21: number;
  /** 立ち上げ時使用量 */
  chemicalCalc31: number;
  /** 立ち下げ時使用量 */
  chemicalCalc32: number;
  /** 非常用発電時使用量 */
  chemicalCalc33: number;
  /** 作成日時 */
  createdAt?: string;
  /** 更新日時 */
  updatedAt?: string;
  /** 削除日時 */
  deletedAt?: string;
}

/** 薬剤情報 */
interface ChemicalDataForm {
  /** 支所コード */
  branchCode: string;
  /** 薬剤ID */
  chemicalId?: number;
  /** 薬剤販売業者ID */
  vendorId: number;
  /** 追加販売業者IDリスト */
  additionalVendorId?: number[];
  /** 薬剤分類ID */
  chemicalTypeId: number;
  /** 薬剤名（画面表示用） */
  chemicalName: string;
  /** 薬剤名（発注用） */
  chemicalName1: string;
  /** 薬剤名（Excel資料２） */
  chemicalName2: string;
  /** 薬剤名（Excel資料３） */
  chemicalName3: string;
  /** ソート順（Excel資料２） */
  chemicalSort2: number;
  /** ソート順（Excel資料３） */
  chemicalSort3: number;
  /** 品目コード */
  chemicalCode: string;
  /** WBS要素 */
  chemicalWbs: string;
  /** 単位 */
  chemicalUnit1: string;
  /** 発注単位 */
  chemicalUnit2: string;
  /** 発注単位補正値 */
  chemicalUnit2Calc: number;
  /** 転記単位 */
  chemicalUnit3: string;
  /** 転記単位補正値 */
  chemicalUnit3Calc: number;
  /** 管理用途 */
  chemicalViewtype: number;
  /** 保管貯槽数 */
  chemicalStorageCount?: number;
  /** 設計原単位 */
  chemicalIntensity: number;
  /** 在庫上限 */
  chemicalLimit: number;
  /** 注意レベル */
  chemicalBorder1: number;
  /** 発注レベル */
  chemicalBorder2: number;
  /** 保管貯槽 在庫上限 */
  chemicalStockLimit?: number[];
  /** 保管貯槽 補給レベル */
  chemicalStockBorder1?: number[];
  /** 保管貯槽 発注レベル */
  chemicalStockBorder2?: number[];
  /** 発注量 */
  chemicalOrdervolume: number;
  /** 最低発注量 */
  chemicalMinOrdervolume: number;
  /** 不燃粗大施設稼働時使用量 */
  chemicalCalc01: number;
  /** 使用量予測算出方法 */
  chemicalCalctype: number;
  /** 在庫下限 */
  chemicalCalc11: number;
  /** 共通停炉時使用量 */
  chemicalCalc12: number;
  /** 定期使用量（１日） */
  chemicalCalc21: number;
  /** 立ち上げ時使用量 */
  chemicalCalc31: number;
  /** 立ち下げ時使用量 */
  chemicalCalc32: number;
  /** 非常用発電時使用量 */
  chemicalCalc33: number;
}

/**
 * 薬剤API
 * @param api APIモジュール
 * @param branchCode 支所コード
 * @returns
 */
export function useChemicalApi(api: API, branchCode: string) {
  const [vendors, setVendors] = useState<VendorData[]>();
  const [types, setTypes] = useState<TypeData[]>();
  const [chemicals, setChemicals] = useState<ChemicalData[]>();

  /**
   * GETリクエスト共通処理
   * @param url リクエストURL
   * @returns APIレスポンス
   */
  async function procGet<T>(url: string): Promise<T> {
    try {
      const result = await api.getRequest<T>(url);
      return result.payload;
    } catch (e) {
      showErrorMessage(MESSAGES.ERROR_GET);
    }
  }

  /**
   * POSTリクエスト共通処理
   * @param url リクエストURL
   * @param body リクエストボディ
   */
  async function procPost(url: string, body: any) {
    try {
      await api.postRequestJson(url, body);
    } catch (e) {
      showErrorMessage(MESSAGES.ERROR_POST);
    }
  }

  /**
   * DELETEリクエスト共通処理
   * @param url リクエストURL
   */
  async function procDelete(url: string) {
    try {
      await api.deleteRequest(url);
    } catch (e) {
      showErrorMessage(MESSAGES.ERROR_DELETE);
    }
  }

  /**
   * 薬剤販売業者情報一覧取得
   * @returns 薬剤販売業者情報データリスト
   */
  async function loadVendors() {
    const params = { branchCode };
    const reqUrl = createUrl(API_CHEMICAL_VENDOR_URL, params);
    const results = await procGet<VendorData[]>(reqUrl);
    const sorted = results?.sort((a, b) => a.vendorId - b.vendorId);
    setVendors(sorted);
  }

  /**
   * 薬剤分類情報一覧取得
   * @returns 薬剤分類情報データリスト
   */
  async function loadTypes() {
    const params = { branchCode };
    const reqUrl = createUrl(API_CHEMICAL_TYPE_URL, params);
    const results = await procGet<TypeData[]>(reqUrl);
    const sorted = results?.sort((a, b) => a.chemicalTypeId - b.chemicalTypeId);
    setTypes(sorted);
  }

  /**
   * 薬剤情報一覧取得
   * @param api APIモジュール
   * @param branchCode 支所コード
   * @returns 薬剤情報データリスト
   */
  async function loadChemicals() {
    const params = { branchCode };
    const reqUrl = createUrl(API_CHEMICAL_URL, params);
    const results = await procGet<ChemicalData[]>(reqUrl);
    const sorted = results?.sort((a, b) => a.chemicalId - b.chemicalId);
    setChemicals(sorted);
  }

  /**
   * 薬剤販売業者情報登録・更新
   * @param vendor 薬剤販売業者情報
   */
  async function saveVendor(vendor: ChemicalVendor) {
    const body: VendorDataForm = { branchCode, ...vendor };
    await procPost(API_CHEMICAL_VENDOR_URL, body);
    await loadVendors();
  }

  /**
   * 薬剤販売業者情報削除
   * @param vendor 薬剤販売業者情報
   */
  async function deleteVendor(vendor: ChemicalVendor) {
    const params = { branchCode, vendorId: vendor.vendorId };
    const reqUrl = createUrl(API_CHEMICAL_VENDOR_URL, params);
    await procDelete(reqUrl);
    await loadVendors();
  }

  /**
   * 薬剤分類情報登録・更新
   * @param type 薬剤分類情報
   */
  async function saveType(type: ChemicalType) {
    const body: TypeDataForm = { branchCode, ...type };
    await procPost(API_CHEMICAL_TYPE_URL, body);
    await loadTypes();
  }

  /**
   * 薬剤分類情報削除
   * @param type 薬剤分類情報
   */
  async function deleteType(type: ChemicalType) {
    const params = { branchCode, chemicalTypeId: type.chemicalTypeId };
    const reqUrl = createUrl(API_CHEMICAL_TYPE_URL, params);
    await procDelete(reqUrl);
    await loadTypes();
  }

  /**
   * 薬剤情報登録・更新
   * @param chemical 薬剤情報
   */
  async function saveChemical(chemical: Chemical) {
    const body: ChemicalDataForm = { branchCode, ...chemical };
    await procPost(API_CHEMICAL_URL, body);
    await loadChemicals();
  }

  /**
   * 薬剤情報削除
   * @param chemical 薬剤情報
   */
  async function deleteChemical(chemical: Chemical) {
    const params = { branchCode, chemicalId: chemical.chemicalId };
    const reqUrl = createUrl(API_CHEMICAL_URL, params);
    await procDelete(reqUrl);
    await loadChemicals();
  }

  const getVendor = useCallback(
    (chemicalId: number) => vendors?.find(v => v.vendorId === getChemical(chemicalId)?.vendorId),
    [vendors, chemicals]
  );
  const getType = useCallback(
    (chemicalTypeId: number) => types?.find(t => t.chemicalTypeId === chemicalTypeId),
    [types]
  );
  const getChemical = useCallback(
    (chemicalId: number) => chemicals?.find(c => c.chemicalId === chemicalId),
    [chemicals]
  );
  const isValidChemical = useCallback(
    (c: Chemical) => getVendor(c?.chemicalId) && getType(c?.chemicalTypeId),
    [chemicals, vendors, types]
  );
  const isValidChemicalId = useCallback(
    (id: number) => isValidChemical(getChemical(id)),
    [chemicals, vendors, types]
  );
  /**
   * 販売業者と分類が存在する薬剤のリストを返す
   */
  const getValidChemicals = useCallback(
    () => chemicals?.filter(c => isValidChemical(c)),
    [chemicals, vendors, types]
  );

  useEffect(() => {
    loadVendors();
    loadTypes();
    loadChemicals();
  }, []);

  const isLoaded = vendors && types && chemicals;

  return [
    {
      isLoaded,
      vendors,
      types,
      chemicals,
      getVendor,
      getType,
      getChemical,
      getValidChemicals,
      isValidChemical: isValidChemicalId,
    },
    { saveVendor, deleteVendor, saveType, deleteType, saveChemical, deleteChemical },
  ];
}
